// @ts-check
/**
 * @file useReentryToast.js
 * @location src/hooks/useReentryToast.js
 * @role Page Visibility API 로 탭 비활성→활성 전환 감지.
 *   임계시간(기본 30분) 이상 비활성 후 재진입 시 onReentry 콜백.
 *
 *  App.jsx 가 콜백에서 미정독 카운트 토스트 띄움.
 *  외부 채널 X — 앱 안에서만 가시화.
 */
import { useEffect, useRef } from 'react';

/**
 * @param {(idleMs: number) => void} onReentry
 * @param {number} [thresholdMs] - 기본 30분
 */
export function useReentryToast(onReentry, thresholdMs = 30 * 60 * 1000) {
  const hiddenAtRef = useRef(/** @type {number | null} */ (null));
  const onReentryRef = useRef(onReentry);
  useEffect(() => { onReentryRef.current = onReentry; }, [onReentry]);

  useEffect(() => {
    const handler = () => {
      if (document.visibilityState === 'hidden') {
        hiddenAtRef.current = Date.now();
      } else if (document.visibilityState === 'visible') {
        const since = hiddenAtRef.current;
        hiddenAtRef.current = null;
        if (since != null) {
          const idle = Date.now() - since;
          if (idle >= thresholdMs) onReentryRef.current(idle);
        }
      }
    };
    document.addEventListener('visibilitychange', handler);
    return () => document.removeEventListener('visibilitychange', handler);
  }, [thresholdMs]);
}
